import { useContext, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faMarker,
  faUsers,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";
import { useHistory, useParams } from "react-router";
import { Modal, Button } from "react-bootstrap";

import { TestAnalysisContext } from "../../contexts/test-analysis.context";
import PageSpinner from "../../_components/page-spinner/PageSpinner";
import ApiService from "../../_services/ApiService";
import AuthenticationService from "../../_services/AuthenticationService";
import { ModalBatchPicker } from "../modal-batch-picker";

import "./test-analysis.styles.css";

interface RouteParams {
  categoryId: string;
}

const TestAnalysisDetail = () => {
  const { getTestDetailsByCategoryId, isLoading } = useContext(TestAnalysisContext);
  const { categoryId } = useParams<RouteParams>();
  const history = useHistory();

  const [batches, setBatches] = useState<any[]>([]);
  const [selectedBatches, setSelectedBatches] = useState<{ [testId: string]: any }>({});
  const [isBatchPickerOpen, setIsBatchPickerOpen] = useState<{ [testId: string]: boolean }>({});
  const [warningOpen, setWarningOpen] = useState(false);

  const testDetails = getTestDetailsByCategoryId(categoryId);

  useEffect(() => {
    const fetchBatches = async () => {
      const currentUser = AuthenticationService?.currentUserValue;
      const instructorId = currentUser?.instructorId;
      try {
        const responseDetails = await ApiService.syncBatches(instructorId);
        setBatches(responseDetails.data?.data || []);
      } catch (err) {
        console.log(err);
      }
    };
    fetchBatches();
  }, []);

  const openBatchPicker = (testId: string) =>
    setIsBatchPickerOpen((prev) => ({ ...prev, [testId]: true }));

  const closeBatchPicker = (testId: string) =>
    setIsBatchPickerOpen((prev) => ({ ...prev, [testId]: false }));

  const goBack = () => history.goBack();

  const handleEnterMarks = (testId: number) => {
    const batch = selectedBatches[testId];
    if (!batch || !batch.id) {
      setWarningOpen(true);
      return;
    }
    history.push(`/testAnalysis/marks/test/${testId}/batch/${batch.id}?source=testAnalysis`);
  };

  if (isLoading) {
    return <PageSpinner />;
  }

  const tests: any[] = (testDetails?.tests as any[] | undefined) || [];

  return (
    <div className="tad-page">
      <header className="tad-header">
        <button
          type="button"
          className="tad-back-btn"
          onClick={goBack}
          aria-label="Go back"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <div className="tad-header-titles">
          <span className="tad-header-eyebrow">Test Analysis</span>
          <h1 className="tad-header-title">{testDetails?.name}</h1>
        </div>
        <span className="tad-header-count">
          {tests.length} {tests.length === 1 ? "test" : "tests"}
        </span>
      </header>

      <main className="tad-main">
        {tests.length === 0 ? (
          <div className="tad-empty">
            <FontAwesomeIcon icon={faExclamationCircle} className="tad-empty-icon" />
            <p>No tests found in this category.</p>
          </div>
        ) : (
          <ul className="tad-list">
            {tests.map((test: any) => {
              const selected = selectedBatches[test.id];
              const hasBatch = !!selected?.id;

              return (
                <li className="tad-card" key={test.id}>
                  <div className="tad-card-body">
                    <div className="tad-card-info">
                      <h2 className="tad-card-title" title={test.name}>
                        {test.name}
                      </h2>
                      <div
                        className={`tad-batch-chip ${hasBatch ? "is-selected" : "is-empty"}`}
                      >
                        <FontAwesomeIcon icon={faUsers} className="tad-batch-chip-icon" />
                        <span className="tad-batch-chip-label">
                          {hasBatch ? selected.name : "No batch selected"}
                        </span>
                      </div>
                    </div>

                    <div className="tad-card-actions">
                      <Button
                        variant="secondary"
                        className="tad-btn tad-btn-ghost"
                        onClick={() => openBatchPicker(test.id)}
                      >
                        {hasBatch ? "Change Batch" : "Pick Batch"}
                      </Button>
                      <Button
                        variant="primary"
                        className="tad-btn tad-btn-primary"
                        onClick={() => handleEnterMarks(test.id)}
                        disabled={!hasBatch}
                        title={hasBatch ? "Enter marks for this test" : "Select a batch to continue"}
                      >
                        <FontAwesomeIcon icon={faMarker} className="tad-btn-icon" />
                        Enter Marks
                      </Button>
                    </div>
                  </div>

                  {isBatchPickerOpen[test.id] && (
                    <ModalBatchPicker
                      my_modal_title="Pick a batch"
                      batches={batches}
                      my_modal_content=""
                      getResult={(result) => {
                        setSelectedBatches((prev) => ({ ...prev, [test.id]: result.batch }));
                      }}
                      close={() => closeBatchPicker(test.id)}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </main>

      <Modal show={warningOpen} onHide={() => setWarningOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Select a batch first</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Please pick a batch for this test before entering marks.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setWarningOpen(false)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TestAnalysisDetail;
